import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Spin, 
  Typography,
  Space
} from 'antd';
import { 
  ShopOutlined, 
  TeamOutlined, 
  DollarOutlined, 
  FileTextOutlined, 
  CheckCircleOutlined,
  CarOutlined
} from '@ant-design/icons';
import { dashboardService } from '../services/api';

const { Title, Text } = Typography;

interface ChartData {
  paymentStatus: {
    labels: string[];
    data: number[];
  };
  monthlyPayments: {
    labels: string[];
    amounts: number[];
    counts: number[];
  };
  complaintsByCategory: {
    labels: string[];
    data: number[];
  };
  attendanceTrend: {
    labels: string[];
    data: number[];
  };
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalShops: 0,
    totalStaff: 0,
    pendingPayments: 0,
    activeComplaints: 0,
    todayAttendance: 0,
    totalVehicles: 0,
  });
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await dashboardService.getStats();
      setStats(response.data.data);
      if (response.data.data.charts) {
        setChartData(response.data.data.charts);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  // Prepare data for charts
  const paymentStatusData = chartData?.paymentStatus
    ? chartData.paymentStatus.labels.map((label, index) => ({
        name: label.charAt(0).toUpperCase() + label.slice(1),
        value: chartData.paymentStatus.data[index] || 0,
      }))
    : [];

  const monthlyPaymentsData = chartData?.monthlyPayments
    ? chartData.monthlyPayments.labels.map((label, index) => ({
        month: label,
        amount: chartData.monthlyPayments.amounts[index] || 0,
        count: chartData.monthlyPayments.counts[index] || 0,
      }))
    : [];

  const complaintsData = chartData?.complaintsByCategory
    ? chartData.complaintsByCategory.labels.map((label, index) => ({
        category: label.charAt(0).toUpperCase() + label.slice(1),
        count: chartData.complaintsByCategory.data[index] || 0,
      }))
    : [];

  const attendanceData = chartData?.attendanceTrend
    ? chartData.attendanceTrend.labels.map((label, index) => ({
        date: label,
        attendance: chartData.attendanceTrend.data[index] || 0,
      }))
    : [];

  const COLORS = ['#667eea', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const statCards = [
    {
      title: 'Total Shops',
      value: stats.totalShops,
      icon: <ShopOutlined className="text-4xl" />,
      color: '#667eea',
    },
    {
      title: 'Total Staff',
      value: stats.totalStaff,
      icon: <TeamOutlined className="text-4xl" />,
      color: '#10b981',
    },
    {
      title: 'Pending Payments',
      value: stats.pendingPayments,
      icon: <DollarOutlined className="text-4xl" />,
      color: '#f59e0b',
    },
    {
      title: 'Active Complaints',
      value: stats.activeComplaints,
      icon: <FileTextOutlined className="text-4xl" />,
      color: '#ef4444',
    },
    {
      title: "Today's Attendance",
      value: stats.todayAttendance,
      icon: <CheckCircleOutlined className="text-4xl" />,
      color: '#8b5cf6',
    },
    {
      title: 'Total Vehicles',
      value: stats.totalVehicles,
      icon: <CarOutlined className="text-4xl" />,
      color: '#06b6d4',
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <Space direction="vertical" size="large" className="w-full">
          {/* Page Header */}
          <div>
            <Title level={2} className="mb-2 text-blue-600">
              Dashboard
            </Title>
            <Text type="secondary" className="text-lg">
              Welcome back! Here's what's happening today.
            </Text>
          </div>

          {/* Stats Cards */}
          <Row gutter={[16, 16]}>
            {statCards.map((card, index) => (
              <Col xs={24} sm={12} lg={8} xl={5} key={index}>
                <Card
                  className="shadow-lg border-0 rounded-xl hover:shadow-xl transition-all duration-300"
                  bodyStyle={{ padding: '24px' }}
                >
                  <Statistic
                    title={card.title}
                    value={card.value}
                    prefix={
                      <div style={{ color: card.color }}>
                        {card.icon}
                      </div>
                    }
                    valueStyle={{ 
                      color: card.color,
                      fontSize: '32px',
                      fontWeight: 'bold'
                    }}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          {/* Charts Grid */}
          <Row gutter={[16, 16]}>
            {/* Payment Status Pie Chart */}
            <Col xs={24} lg={12}>
              <Card
                title="Payment Status Distribution"
                className="shadow-lg border-0 rounded-xl"
              >
                {paymentStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={paymentStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(props: any) => {
                          const { name, percent } = props;
                          if (name && percent !== undefined) {
                            return `${name}: ${(percent * 100).toFixed(0)}%`;
                          }
                          return '';
                        }}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {paymentStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-16 text-gray-500">
                    No payment data available
                  </div>
                )}
              </Card>
            </Col>

            {/* Monthly Payments Area Chart */}
            <Col xs={24} lg={12}>
              <Card
                title="Monthly Payments Trend"
                className="shadow-lg border-0 rounded-xl"
              >
                {monthlyPaymentsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={monthlyPaymentsData}>
                      <defs>
                        <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#667eea" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" stroke="#6b7280" />
                      <YAxis
                        stroke="#6b7280"
                        tickFormatter={(value) => `₹${value / 1000}k`}
                      />
                      <Tooltip
                        formatter={(value: number | undefined) =>
                          value !== undefined ? `₹${value.toLocaleString()}` : ''
                        }
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke="#667eea"
                        fillOpacity={1}
                        fill="url(#colorAmount)"
                        name="Total Amount"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-16 text-gray-500">
                    No payment trend data available
                  </div>
                )}
              </Card>
            </Col>

            {/* Complaints Bar Chart */}
            <Col xs={24} lg={12}>
              <Card
                title="Complaints by Category"
                className="shadow-lg border-0 rounded-xl"
              >
                {complaintsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={complaintsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="category" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="count" fill="#667eea" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-16 text-gray-500">
                    No complaints data available
                  </div>
                )}
              </Card>
            </Col>

            {/* Attendance Trend Line Chart */}
            <Col xs={24} lg={12}>
              <Card
                title="Attendance Trend (Last 7 Days)"
                className="shadow-lg border-0 rounded-xl"
              >
                {attendanceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={attendanceData}>
                      <defs>
                        <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="attendance"
                        stroke="#8b5cf6"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorAttendance)"
                      />
                      <Line
                        type="monotone"
                        dataKey="attendance"
                        stroke="#8b5cf6"
                        strokeWidth={3}
                        dot={{ fill: '#8b5cf6', r: 6 }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-16 text-gray-500">
                    No attendance data available
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Space>
      </div>
    </div>
  );
};

export default Dashboard;
